<template>
  <div
    v-if="(isBoardOwner || isSharedWithMe) && !isLoading"
    class="d-flex align-center flex-grow-1 justify-end"
  >
    <template v-for="(sharedAccount, index) in shares">
      <v-tooltip bottom :key="sharedAccount.id">
        <template v-slot:activator="{ on }">
          <div
            v-on="on"
            class="cursor-pointer"
            @click="showShareModal = isBoardOwner"
          >
            <UserAvatar
              class="shared-item"
              size="32px"
              fontSize="10px"
              :class="{ 'ml-3': index === 0 }"
              :account="sharedAccount"
            />
          </div>
        </template>
        {{ getFullName(sharedAccount) }}
      </v-tooltip>
    </template>

    <v-menu v-if="moreShares.length" open-on-hover>
      <template v-slot:activator="{ on }">
        <div
          v-on="on"
          class="shared-item more-items-selected blue white--text d-flex justify-center align-center chip-over-chip font-weight-600 font-size-10 cursor-pointer"
          @click="showShareModal = isBoardOwner"
        >
          + {{ moreShares.length }}
        </div>
      </template>

      <v-card
        class="ma-0 pa-0 blue-grey lighten-3 px-4 pt-4 pb-1 item-detail"
        dense
        elevation="2"
      >
        <p
          v-for="(item, index) in moreShares"
          class="ma-0 pa-0 label mb-3 blue-grey--text text--darken-2 font-weight-400"
          :key="`${item}${index}`"
        >
          {{ getFullName(item) }}
        </p>
      </v-card>
    </v-menu>

    <Button
      v-if="isBoardOwner"
      plain
      class="share-button ml-4"
      icon="mdi-share-variant-outline"
      iconPosition="left"
      :text="$t('boards.share')"
      @click="handleShare"
    />

    <Button
      v-if="isSharedWithMe"
      plain
      class="share-button ml-4"
      icon="mdi-account-minus-outline"
      iconPosition="left"
      :text="$t('common.unfollow')"
      @click="showDeleteModal = true"
    />
    <ShareBoardModal
      v-if="showShareModal"
      :boardId="board.id"
      @close="showShareModal = false"
      @save="getSharedAccounts()"
    />
    <DeleteConfirmationModal
      :show="showDeleteModal"
      :title="$t('boards.unfollow_board')"
      :text="$t('boards.unfollow_board_message', { name: board.name })"
      :confirmText="$t('common.unfollow')"
      :isLoading="isLoading"
      @cancel="showDeleteModal = false"
      @delete="handleUnfollow"
    />
  </div>
  <div
    v-else-if="isLoading"
    class="d-flex align-center flex-grow-1 justify-end shared-skeletons ml-4"
  >
    <v-skeleton-loader class="share-avatar shared-item" type="avatar" />
    <v-skeleton-loader class="share-avatar shared-item mr-4" type="avatar" />
    <v-skeleton-loader type="chip" width="96px" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import userMixin from '@/mixins/userMixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import UserAvatar from '@/components/UserAvatar.vue'
import Button from '@/components/UI/Button.vue'
import ShareBoardModal from '@/components/Modals/ShareBoardModal.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

export default {
  name: 'ShareSettings',

  mixins: [mixin, userMixin, trackerMixin],

  components: {
    UserAvatar,
    Button,
    ShareBoardModal,
    DeleteConfirmationModal,
  },

  data() {
    return {
      showShareModal: false,
      showDeleteModal: false,
      isLoading: false,
      shareList: [],
    }
  },

  props: {
    board: {
      type: Object,
      required: true,
    },
  },

  mounted() {
    this.getSharedAccounts()
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId']),

    shares() {
      return this.shareList?.slice(0, 1) ?? []
    },

    moreShares() {
      return this.shareList?.slice(1) ?? []
    },

    totalShares() {
      return this.shareList.length
    },

    isBoardOwner() {
      return this.accountId === this.board?.createdBy
    },

    isSharedWithMe() {
      return Boolean(this.board?.isShared)
    },
  },

  methods: {
    handleShare() {
      this.trackBoardsActions('boards.ShareAction', {
        section: 'all boards',
        action: 'open share board modal',
      })
      this.showShareModal = true
    },

    async handleUnfollow() {
      try {
        this.trackBoardsActions('boards.ConfirmAction', {
          action: 'Unfollow modal',
        })
        this.isLoading = true
        await api.board.unfollow(
          this.organizationId,
          this.board?.id,
          this.accountId
        )
        this.showDeleteModal = false
        this.isLoading = false
        this.showSuccess(this.$t('boards.board_successfully_unfollowed'))
        this.navigateTo({ name: 'boards' })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async getSharedAccounts() {
      try {
        this.showShareModal = false
        this.isLoading = true
        const shareListResponse = await api.board.getShareList(
          this.organizationId,
          this.board?.id
        )
        this.shareList = shareListResponse?.data?.results ?? []
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.shared-item {
  margin-left: -6px;
  z-index: 1;
}
.more-items-selected {
  width: 32px;
  height: 32px;
  border-radius: 16px;
  z-index: 1;
}
</style>

<style lang="scss">
.share-button {
  .v-btn__content {
    opacity: 1 !important;
  }
}
.share-avatar .v-skeleton-loader__avatar {
  height: 32px !important;
  width: 32px !important;
}
.shared-skeletons .v-skeleton-loader .v-skeleton-loader__bone {
  background: $color-blue-grey-lighten2 !important;
}
</style>
